import { type ChangeEvent, memo, useCallback, useId, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useDebounce } from 'use-debounce';
import { FieldStatus } from '@m/alchemy-ui/FieldStatus';
import { Input } from '@m/alchemy-ui/Input';
import { Select } from '@m/alchemy-ui/Select';
import { TextArea } from '@m/alchemy-ui/TextArea';
import { VideoEmbedTypeEnum } from '../../../../common/core-api';
import { type IPageItem } from '../../../../common/interfaces';
import { Video } from '../../../community-page';
import { type PanelConfigPatch } from '../../common/panel-grid';
import { type PageCmsPanel } from '../../common/panel-types';
import { getVideoPanelError, VALIDATION_DEBOUNCE_MS } from '../../common/panel-validation';
import {
  buildSafeEmbedCode,
  buildVideoTagEmbedCode,
  generateEmbedCodeFromUrl,
  isHttpUrl,
  parseEmbedCode
} from '../../common/video-embed';
import { PanelEditorLabel, PanelEditorSection, PanelEditorStretchSection, VideoPreview } from '../../CustomPage.styled';
import { panelEditorMessages } from './panel-editor-messages';

const messages = defineMessages({
  embedWith: {
    id: 'embedWith',
    defaultMessage: 'Embed with'
  },
  linkToVideo: {
    id: 'linkToVideo',
    defaultMessage: 'Link to video'
  },
  iframeCode: {
    id: 'iframeCode',
    defaultMessage: 'iFrame code'
  },
  htmlVideoTag: {
    id: 'htmlVideoTag',
    defaultMessage: 'HTML Video tag'
  },
  videoLink: {
    id: 'videoLink',
    defaultMessage: 'Video link'
  },
  embedCode: {
    id: 'editCustomPageEmbedCode',
    defaultMessage: 'Embed code'
  }
});

type VideoConfig = Extract<PageCmsPanel['settings'], { __typename: 'VideoPanelSettings' }>;

interface VideoPanelEditorProps {
  readonly config: VideoConfig;
  readonly onConfigChange: (patch: PanelConfigPatch) => void;
}

const VideoPanelEditorComponent = ({ config, onConfigChange }: VideoPanelEditorProps) => {
  const intl = useIntl();
  const id = useId();
  const embedLabelId = `video-embed-label-${id}`;
  const urlInputId = `video-url-${id}`;
  const embedCodeInputId = `video-embed-code-${id}`;
  const { embed, videoUrl, videoEmbedCode } = config;

  const videoFallbackText = intl.formatMessage(panelEditorMessages.browserDoesNotSupportVideo);

  const embedOptions = useMemo(
    () => [
      { value: VideoEmbedTypeEnum.LINK as string, label: intl.formatMessage(messages.linkToVideo) },
      { value: VideoEmbedTypeEnum.CODE as string, label: intl.formatMessage(messages.iframeCode) },
      { value: VideoEmbedTypeEnum.VIDEO_TAG as string, label: intl.formatMessage(messages.htmlVideoTag) }
    ],
    [intl]
  );

  // Config writes stay immediate; only the preview/error evaluation is debounced. The whole
  // config is debounced as one value so the embed type and the code/url never mix stale and
  // fresh state (which would flash a wrong error while switching embed type).
  const [debouncedConfig] = useDebounce(config, VALIDATION_DEBOUNCE_MS);
  const debouncedEmbedCode = debouncedConfig.videoEmbedCode ?? '';

  const parsedEmbed = useMemo(() => parseEmbedCode(debouncedEmbedCode), [debouncedEmbedCode]);
  const showVideoInput = embed === VideoEmbedTypeEnum.LINK || embed === VideoEmbedTypeEnum.VIDEO_TAG;

  // Preview through the public community-page renderer for exact display parity.
  const previewItem = useMemo(
    () =>
      parsedEmbed
        ? ({
            id: 'video-panel-preview',
            settings: { type: 'video', videoEmbedCode: buildSafeEmbedCode(parsedEmbed, videoFallbackText) }
          } as IPageItem)
        : null,
    [parsedEmbed, videoFallbackText]
  );

  const errorMessage = useMemo(() => getVideoPanelError(debouncedConfig), [debouncedConfig]);

  const generateForEmbedType = useCallback(
    (embedType: VideoEmbedTypeEnum, url: string) =>
      embedType === VideoEmbedTypeEnum.VIDEO_TAG
        ? isHttpUrl(url)
          ? buildVideoTagEmbedCode(url, videoFallbackText)
          : ''
        : (generateEmbedCodeFromUrl(url) ?? ''),
    [videoFallbackText]
  );

  const handleEmbedTypeChange = useCallback(
    (option: { value: string }) => {
      const nextEmbed = option.value as VideoEmbedTypeEnum;
      onConfigChange({
        embed: nextEmbed,
        // Regenerate from the current URL so the preview matches the selected mode; switching to
        // "iFrame code" keeps the generated markup in the textarea (legacy behavior).
        ...(nextEmbed !== VideoEmbedTypeEnum.CODE && videoUrl?.trim()
          ? { videoEmbedCode: generateForEmbedType(nextEmbed, videoUrl) }
          : {})
      });
    },
    [videoUrl, generateForEmbedType, onConfigChange]
  );

  const handleUrlChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const url = event.target.value;
      onConfigChange({
        videoUrl: url,
        videoEmbedCode: url.trim() ? generateForEmbedType(embed, url) : ''
      });
    },
    [embed, generateForEmbedType, onConfigChange]
  );

  const handleEmbedCodeChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) =>
      // Stored raw; validity is derived for the preview, and the future save mutation must still
      // sanitise embed code server-side.
      onConfigChange({ videoEmbedCode: event.target.value }),
    [onConfigChange]
  );

  return (
    <>
      {(previewItem || errorMessage) && (
        <PanelEditorStretchSection>
          {previewItem ? (
            <VideoPreview>
              <Video item={previewItem} />
            </VideoPreview>
          ) : (
            errorMessage && <FieldStatus level="error" message={intl.formatMessage(errorMessage)} />
          )}
        </PanelEditorStretchSection>
      )}
      <PanelEditorSection>
        <PanelEditorLabel id={embedLabelId}>{intl.formatMessage(messages.embedWith)}</PanelEditorLabel>
        <Select
          labelId={embedLabelId}
          isFullWidth
          value={embed}
          options={embedOptions}
          onChange={handleEmbedTypeChange}
        />
      </PanelEditorSection>
      {showVideoInput ? (
        <PanelEditorSection>
          <PanelEditorLabel as="label" htmlFor={urlInputId}>
            {intl.formatMessage(messages.videoLink)}
          </PanelEditorLabel>
          <Input id={urlInputId} isFullWidth value={videoUrl ?? ''} onChange={handleUrlChange} />
        </PanelEditorSection>
      ) : (
        <PanelEditorStretchSection>
          <PanelEditorLabel as="label" htmlFor={embedCodeInputId}>
            {intl.formatMessage(messages.embedCode)}
          </PanelEditorLabel>
          <TextArea id={embedCodeInputId} isFullWidth value={videoEmbedCode ?? ''} onChange={handleEmbedCodeChange} />
        </PanelEditorStretchSection>
      )}
    </>
  );
};

// Immutable config updates make prop comparison cheap: the editor re-renders only when its
// own config (or the grid callback) changes.
export const VideoPanelEditor = memo(VideoPanelEditorComponent);
